import React, { useEffect, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { useAppDispatch, useAppSelector } from '../../store';
import CustomTable from '../misc/CustomTable';
import {
  selectCurrentOrganizationId,
  selectOrganizationsData,
} from '../../store/organization/organization-slice-selectors';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { getOrganizationRobots } from '../../store/robots/robots-actions';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import useCustomLocalizationEffect from '../../hooks/useCustomLocalizationEffect';
import useFetchAll from '../../hooks/useFetchAll';
import RobotCard, { getRobotStatusIcon } from './RobotCard';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import {
  AddIcon,
  FilterIcon,
  GridIcon,
  NotSubscribedIcon,
  OpenInNewTabIcon,
  SubscribedIcon,
  TableIcon,
} from '@common/utils/icons/icons';
import RobotForm from './RobotForm';
import OrganizationRole from '../../models/enums/organization-roles';
import { selectIsUserAdminOrHigher, selectUser } from '../../store/general/general-slice-selectors';
import Skeleton from '@mui/material/Skeleton';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import ItemSwitch from '../forms/ItemSwitch';
import Tooltip from '@mui/material/Tooltip';
import CustomLocalizationType from '../../models/enums/custom-localization-type';
import FiltersDialog from '../organizations/FiltersDialog';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { resetRobotsRestState } from '../../store/robots/robots-slice';
import { postSubscribeToOrganizationById } from '../../store/general/general-actions';
import { replaceArgsInMessage } from '../../utils/strings';
import { ContextMenuOption } from '../../models/misc/context-menu';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material';
import useMediaQuery from "@mui/material/useMediaQuery";

const RobotsTable: React.FC = () => {
  const { t } = useTranslation();
  const localize = useCustomLocalizationEffect();

  const dispatch = useAppDispatch();

  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterParams, setFilterParams] = useSearchParams();
  const { fetchAll: queryGetAllRobots, saveFetchAll, clearFetchAll } = useFetchAll('robots');
  const queryPage = filterParams.get(FilterKeys.Page);
  const queryPageSize = parseInt(filterParams.get(FilterKeys.PageSize) ?? '20');
  const queryEndedDeployments = filterParams.get(FilterKeys.EndedDeployments) === 'true';

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const user = useAppSelector((state) => selectUser(state));
  const isSubscribedToOrganization = user.data.organizationSubscriptions.some(
    (org) => org.id === organizationData.current?.id,
  );

  const [isTableView, setIsTableView] = useState(false);
  const [isAddRobotFormOpen, setIsAddRobotFormOpen] = useState(false);
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState(false);

  useEffect(() => {
    if (currentOrganizationId != null) {
      dispatch(
        getOrganizationRobots({
          org: currentOrganizationId,
          all: queryGetAllRobots,
          page: queryPage ?? '1',
          ps: queryPageSize.toString(),
          ended: queryEndedDeployments,
        }),
      );
    }
  }, [
    currentOrganizationId,
    dispatch,
    queryGetAllRobots,
    queryPage,
    queryPageSize,
    queryEndedDeployments,
  ]);

  useEffect(() => {
    dispatch(resetRobotsRestState({ getStatus: HttpFetchStatus.Idle }));
  }, [dispatch, queryGetAllRobots]);

  const rowClickHandler = (id: string) => {
    robotClickHandler(id);
  };

  const robotClickHandler = (id: string) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.RobotId, id);
      return prevState;
    });
  };

  const newTabClickHandler = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(FilterKeys.RobotId, id);
    window.open(url.toString(), '_blank');
  };

  const toggleAllClickHandler = () => {
    saveFetchAll(!queryGetAllRobots);
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.All, (!queryGetAllRobots).toString());
      prevState.delete(FilterKeys.Page);
      return prevState;
    });
  };

  const toggleEndedDeploymentsClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.EndedDeployments, (!queryEndedDeployments).toString());
      prevState.delete(FilterKeys.Page);
      return prevState;
    });
  };

  const renderRobotStatusIcon = (params: GridRenderCellParams) => {
    const robot = robotsData.pagination?.data?.find((r) => r.id === params.value);
    return getRobotStatusIcon(t, localize, 'medium', robot?.status);
  };

  const columns: GridColDef[] = [
    {
      field: 'active',
      headerName: '',
      renderCell: renderRobotStatusIcon,
      valueGetter: (_, row) => {
        return row.id;
      },
      sortable: false,
      width: 50,
    },
    { field: 'name', headerName: t('table-header-name') as string, flex: 1 },
    { field: 'serialNumber', headerName: t('table-header-serial-number') as string, flex: 1 },
    {
      field: 'state',
      headerName: t('table-header-robot-state') as string,
      flex: 1,
      width: 75,
      valueGetter: (_, row) => {
        if (row.status != null && row.status.active) {
          return localize(row.status.state, CustomLocalizationType.STATE);
        }
        return '-';
      },
    },
    {
      field: 'battery',
      headerName: t('table-header-robot-battery-percentage') as string,
      flex: 1,
      width: 50,
      valueGetter: (_, row) => {
        if (row.status != null && row.status.active) {
          return `${row.status.batteryPercentage}%`;
        }
        return '-';
      },
    },
    {
      field: 'location',
      headerName: t('table-header-location') as string,
      flex: 1,
      valueGetter: (_, row) => {
        return row.deploymentDates.organizationName;
      },
    },
  ];

  const toggleViewClickHandler = () => {
    setIsTableView((prevState) => !prevState);
  };

  const openAddRobotFormClickHandler = () => {
    setIsAddRobotFormOpen(true);
  };

  const closeAddRobotFormHandler = () => {
    setIsAddRobotFormOpen(false);
  };

  const openFiltersDialogClickHandler = () => {
    setIsFiltersDialogOpen(true);
  };

  const toggleSubscribeClickHandler = () => {
    if (organizationData.current?.id != null) {
      dispatch(postSubscribeToOrganizationById(organizationData.current.id));
    }
  };

  const closeFiltersDialogHandler = () => {
    setIsFiltersDialogOpen(false);
  };

  const resetFiltersHandler = () => {
    clearFetchAll();
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.All);
      prevState.delete(FilterKeys.EndedDeployments);
      return prevState;
    });
  };

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.Page, (newPage + 1).toString());
      return prevState;
    });
  };

  const handleRobotsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value);
    if (newSize !== queryPageSize) {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.PageSize, newSize.toString());
        if (
          robotsData.pagination?.count &&
          Math.ceil(robotsData.pagination?.count / newSize) < parseInt(queryPage ?? '1')
        ) {
          prevState.set(
            FilterKeys.Page,
            Math.ceil(robotsData.pagination?.count / newSize).toString(),
          );
        }
        return prevState;
      });
    }
  };

  const handlePageChangeTableView = (newPage: number, newSize: number) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.Page, (newPage + 1).toString());
      prevState.set(FilterKeys.PageSize, newSize.toString());
      return prevState;
    });
  };

  const contextMenuOptions: ContextMenuOption[] = [
    {
      title: t('button-open-in-new-tab'),
      icon: <OpenInNewTabIcon color="action" />,
      action: newTabClickHandler,
    },
  ];

  const currentRobots = robotsData.pagination?.data ?? [];

  return (
    <React.Fragment>
      <Stack spacing={1}>
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            {isAdmin && user.data.organization.role === OrganizationRole.MANUFACTURER && (
              <Button startIcon={<AddIcon />} onClick={openAddRobotFormClickHandler}>
                {t('button-add-robot')}
              </Button>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {matchesSmDown &&
              <TooltipIconButton
                tooltip={replaceArgsInMessage(
                  isSubscribedToOrganization ? t('button-subscribed') : t('button-not-subscribed'),
                  [organizationData.current?.name ?? ''],
                )}
                onClick={toggleSubscribeClickHandler}
                icon={isSubscribedToOrganization ? <SubscribedIcon /> : <NotSubscribedIcon />}
                color="primary"
              />
            }
            <TooltipIconButton
              tooltip={t('info-tooltip-open-filters')}
              icon={<FilterIcon color="primary" />}
              onClick={openFiltersDialogClickHandler}
            />
            {!matchesSmDown && (queryGetAllRobots || queryEndedDeployments) &&
              <Stack direction="row" alignItems="center" spacing={1}>
                {queryGetAllRobots && (
                  <Chip onDelete={toggleAllClickHandler} label={t('input-label-fetch-all')} />
                )}
                {queryEndedDeployments && (
                  <Chip
                    onDelete={toggleEndedDeploymentsClickHandler}
                    label={t('title-ended-deployments')}
                  />
                )}
              </Stack>
            }
            <ItemSwitch
              itemLeft={
                <Tooltip title={t('info-tooltip-card-view')}>
                  <GridIcon />
                </Tooltip>
              }
              itemRight={
                <Tooltip title={t('info-tooltip-table-view')}>
                  <TableIcon />
                </Tooltip>
              }
              leftChecked={!isTableView}
              onChange={toggleViewClickHandler}
            />
          </Stack>
        </Stack>
        {matchesSmDown && (queryGetAllRobots || queryEndedDeployments) &&
          <Stack direction="row" justifyContent="flex-end"flexWrap="wrap" gap={1}>
            {queryGetAllRobots && (
              <Chip onDelete={toggleAllClickHandler} label={t('input-label-fetch-all')} />
            )}
            {queryEndedDeployments && (
              <Chip
                onDelete={toggleEndedDeploymentsClickHandler}
                label={t('title-ended-deployments')}
              />
            )}
          </Stack>
        }
        {isTableView ? (
          <CustomTable
            getStatus={robotsData.rest.getStatus}
            loadingMessage={t('loading-robots')}
            dataLoaded={currentRobots != null}
            failedToLoadMessage={robotsData.rest.getError?.message ?? t('error-loading-robots')}
            data={currentRobots}
            emptyDataMessage={t('info-no-robots')}
            columns={columns}
            autoHeight
            rowClickHandler={rowClickHandler}
            contextMenuOptions={contextMenuOptions}
            customLabelRowsPerPage={t('label-robots-per-page')}
            pagination
            page={queryPage ? parseInt(queryPage) - 1 : 0}
            pageSize={queryPageSize}
            onPageChange={handlePageChangeTableView}
            rowCount={robotsData.pagination?.count}
          />
        ) : (
          <Stack
            direction="row"
            spacing={2}
            overflow="auto"
            p={robotsData.pagination != null && currentRobots.length > 0 ? 0.2 : 0}
            pb={robotsData.pagination != null && currentRobots.length > 0 ? 2 : 0}
          >
            {robotsData.rest.getStatus === HttpFetchStatus.Failed ? (
              <Box width="100%">
                <CustomAlert
                  severity="error"
                  header={robotsData.rest.getError?.message ?? t('error-loading-robots')}
                />
              </Box>
            ) : robotsData.rest.getStatus === HttpFetchStatus.Loading ||
              robotsData.rest.getStatus === HttpFetchStatus.Idle ? (
              <Stack width="100%">
                <Grid container spacing={2}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => (
                    <Grid item sm={12} md={6} lg={4} xl={3} key={index} width="100%">
                      <Skeleton
                        data-testid={'robot-skeleton-' + index}
                        variant="rounded"
                        sx={{
                          width: '100%',
                          height: '295px',
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            ) : currentRobots.length === 0 ? (
              <Box width="100%">
                <CustomAlert severity="info" header={t('info-no-robots')} />
              </Box>
            ) : (
              <Stack width="100%">
                <Grid container spacing={2}>
                  {currentRobots?.map((robot) => (
                    <Grid item sm={12} md={6} lg={4} xl={3} key={robot.id} width="100%">
                      <RobotCard
                        robot={robot}
                        onClick={robotClickHandler}
                        contextMenuOptions={contextMenuOptions}
                      />
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  component="div"
                  count={robotsData.pagination?.count ?? 0}
                  page={queryPage ? parseInt(queryPage) - 1 : 0}
                  onPageChange={handlePageChange}
                  rowsPerPage={queryPageSize}
                  onRowsPerPageChange={handleRobotsPerPageChange}
                  rowsPerPageOptions={[20, 50, 100]}
                  labelRowsPerPage={t('label-robots-per-page')}
                />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      <RobotForm open={isAddRobotFormOpen} onClose={closeAddRobotFormHandler} />
      <FiltersDialog
        open={isFiltersDialogOpen}
        onClose={closeFiltersDialogHandler}
        onResetFilter={resetFiltersHandler}
        allHandler={
          organizationData.current?.role !== OrganizationRole.LOCATION
            ? { all: queryGetAllRobots, onToggle: toggleAllClickHandler }
            : undefined
        }
        endedDeploymentsHandler={{
          showEndedDeployments: queryEndedDeployments,
          onToggle: toggleEndedDeploymentsClickHandler,
        }}
      />
    </React.Fragment>
  );
};

export default RobotsTable;
